import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';
import React from 'react';
import { Dialog } from 'components/feedback/Dialog/Dialog';
import { Button } from 'components/ui/Button';
import { Sizes } from 'enums/sizes';
import { FormikIbanInput } from 'components/forms/formik/FormikIbanInput';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { requiredIbanSchema } from 'shared/utils/ibanSchema';
import { useApiLoader } from 'features/loaders/hooks/useApiLoader';
import { CreateBankAccountCommand } from 'api/bank-account';
import { MandatePayer, MandatePayerOptionDto } from 'api/quote';
import { bankAccountsAPI } from 'services/swagger-api';
import { useQuoteContractualizationData, useQuoteMandateActions } from '../../store';

interface MandateAddFormProps {
  open: boolean;
  onCancel: () => void;
  /**
   * Payeur choisi : le compte est créé sur sa personne (souscripteur ou bénéficiaire), ou pour
   * le cabinet du courtier connecté.
   */
  payerOption: MandatePayerOptionDto;
}

const validationSchema = Yup.object().shape({
  iban: requiredIbanSchema,
  bic: Yup.string().max(11, 'Le BIC ne peut pas dépasser 11 caractères'),
  label: Yup.string().required('Le libelle est requis')
});

/**
 * Modal component for creating a new SEPA mandate
 */
export const BankAccountAddForm: React.FC<MandateAddFormProps> = ({
  open,
  onCancel,
  payerOption
}) => {
  const quoteData = useQuoteContractualizationData();
  const { callWithLoading } = useApiLoader();
  const { fetchPayerBankAccounts } = useQuoteMandateActions();
  const isBrokerPayer = payerOption.code === MandatePayer.Courtier;

  const onSubmit = async (values: { iban: string; bic: string; label: string }) => {
    if (quoteData && (isBrokerPayer || payerOption.personId)) {
      const cleanIban = values.iban.replace(/\s/g, '');
      const createBankAccountCommand: CreateBankAccountCommand = {
        iban: cleanIban,
        bic: values.bic || undefined,
        label: values.label,
        isDefault: true,
        personId: payerOption.personId ?? ''
      };

      const ADD_IBAN_FORM = 'add-iban-form';
      const result = await callWithLoading(
        ADD_IBAN_FORM,
        () =>
          isBrokerPayer
            ? bankAccountsAPI.createMyBankAccount({
                createMyBankAccountCommand: {
                  iban: cleanIban,
                  bic: values.bic || undefined,
                  label: values.label,
                  isDefault: false
                }
              })
            : bankAccountsAPI.createBankAccount({ createBankAccountCommand }),
        {
          captureError: true,
          isGlobalError: true, // Use global error handling for submit operations
          errorTransform: err => {
            return err instanceof Error
              ? err
              : new Error(
                  'Une erreur est survenue lors de la soumission du formulaire'
                );
          }
        }
      );
      if (result && result.isSuccess) {
        fetchPayerBankAccounts(payerOption);
        onCancel();
      } else {
        if (result?.errorMessage) toast.error(result.errorMessage);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} className="z-50">
      <h1 className="font-20 text-dark font-w700 text-center mb-4">
        Ajout d'un nouvel IBAN
      </h1>
      <Formik<{ iban: string; bic: string; label: string }>
        initialValues={{ iban: '', bic: '', label: '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ setFieldValue }) => (
          <Form className="flex flex-col gap-6">
            <FormikInputText
              label="Libelle"
              required
              type="text"
              name="label"
              placeholder="Libelle"
            />
            <FormikIbanInput label="IBAN" required name="iban" />
            <FormikInputText
              label="BIC"
              type="text"
              name="bic"
              placeholder="BNPAFRPP"
              onChange={(e: any) => {
                setFieldValue('bic', e.target.value.toUpperCase());
              }}
            />
            <div className="flex grow gap-4 justify-center">
              <Button size={Sizes.lg} outline onClick={onCancel}>
                Annuler
              </Button>
              <Button color="primary" type="submit" size={Sizes.lg}>
                Valider
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
